import { TFunction } from 'i18next';
import { AiDocumentationFieldEnum } from 'graphql-common';
import { FieldChangeCallBackArgs, FieldItem } from '@lib/interfaces/form';
import Select from '@lib/components/Select/Select';
import { GetLoadOptions } from '@lib/components/Select/types';
import ChipValueContainer from '@lib/components/Select/components/ChipValueContainer';
import ChipOption from '@lib/components/Select/components/ChipOption';
import FieldTitleRender from '@lib/components/FieldTitleRender/FieldTitleRender';
import DocumentFormFieldNames from '@lib/enums/fieldNames/documentFormFieldNames';
import { getOptionValidation } from '@lib/utils/validationUtils';
import { AiCredits } from 'components/AiAssistant/AiCreditsIndicator';
import { AiFieldUiState } from 'routes/Documentation/utils/getAiFieldDetectionState';
import AiFieldAssist from '../components/AiFieldAssist';

export default function getCategoryFields({
  t,
  categoriesLoadOptions,
  showTitle = false,
  isFullAdmin = true,
  isPlanLocked,
  hasCategoriesAccess = true,
  openAddCategoryModal,
  categoryCacheKey,
  isAiAssistVisible,
  isEdit,
  isImport,
  documentationVersionId,
  aiCredits,
  detectionUiState,
  onDismiss,
}: {
  t: TFunction<'translation', undefined>;
  categoriesLoadOptions: GetLoadOptions;
  showTitle?: boolean;
  isFullAdmin?: boolean;
  isPlanLocked?: boolean;
  hasCategoriesAccess?: boolean;
  openAddCategoryModal?: () => void;
  categoryCacheKey?: number;
  // AI auto-fill — same contract as the other AiFieldAssist-backed fields in
  // getDocumentFormFields.tsx; omitted (create-flow default) hides the
  // affordance entirely.
  isAiAssistVisible?: (values: Record<string, unknown>) => boolean;
  isEdit?: boolean;
  isImport?: boolean;
  documentationVersionId?: string;
  aiCredits?: AiCredits | null;
  detectionUiState?: AiFieldUiState;
  onDismiss?: () => void;
}): FieldItem[] {
  if (!hasCategoriesAccess) return [];
  // Locked field is a Full-Admin-only upgrade affordance; hide for User role.
  if (isPlanLocked && !isFullAdmin) return [];
  return [
    ...(showTitle
      ? [
          {
            name: 'categoryTitle',
            customRender: () => (
              <FieldTitleRender title={t('category')} key="categoryTitle" />
            ),
          } as FieldItem,
        ]
      : []),
    {
      name: DocumentFormFieldNames.Category,
      label: t('category'),
      element: Select,
      validation: getOptionValidation({ t }),
      componentProps: {
        isAsync: true,
        getLoadOptions: categoriesLoadOptions,
        defaultOptions: false,
        isClearable: !!isImport,
        maxMenuHeight: 200,
        usePortal: true,
        cacheUniqs: [categoryCacheKey],
        createNewOptionProps:
          openAddCategoryModal && !isPlanLocked
            ? {
                title: t('create-new-category'),
                onCLick: openAddCategoryModal,
              }
            : undefined,
        components: {
          ValueContainer: ChipValueContainer,
          Option: ChipOption,
        },
        disabled: isPlanLocked,
        disabledTooltipProps: isPlanLocked
          ? { body: t('unlock-categories-tooltip') }
          : undefined,
      },
      getComponentProps: (values) => {
        if (!isAiAssistVisible?.(values)) return undefined;
        // Not isClearable, so the built-in DropdownIndicator is the only
        // other indicator ever present — slot 2 (adornment + dropdown).
        return {
          rightAdornmentSlot: 2,
          rightAdornment: (
            <AiFieldAssist
              field={AiDocumentationFieldEnum.Category}
              targetFieldName={DocumentFormFieldNames.Category}
              isEdit={isEdit}
              documentationVersionId={documentationVersionId}
              creditsRemaining={aiCredits}
              detectionUiState={detectionUiState}
              detectingLabel={t('ai-detecting-code')}
              onDismiss={onDismiss}
              labels={{
                idleLabel: t('ai-suggest-with-ai'),
                idleCta: t('ai-suggest-category-cta'),
                regenerateCta: t('ai-regenerate'),
              }}
            />
          ),
        };
      },
      fieldChangeCallBack: ({
        type,
        setValue,
        name,
      }: FieldChangeCallBackArgs) => {
        if (type === 'change' && name === DocumentFormFieldNames.Category) {
          setValue(DocumentFormFieldNames.Approver, null, {
            shouldValidate: false,
          });
        }
      },
    },
  ];
}
